import React from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Panel } from "primereact/panel";
import type { KpiMonth, ScoreDetail, ScoreParameters } from "../../../../types/chargingStationDashboard";
import { formatNumber, formatPercent } from "../../../../utils/fleetDerivations";
import type { TermRow } from "../../../../utils/scorePresentation";
import {
  formatScore,
  monetizationRows,
  operationalRows,
  overallFormula,
  TERM_LABEL,
  unavailableReasons,
} from "../../../../utils/scorePresentation";

interface ScoreBreakdownProps {
  month: KpiMonth;
  parameters: ScoreParameters;
}

const TermTable: React.FC<{ rows: TermRow[]; caption: string }> = ({ rows, caption }) => (
  <DataTable value={rows} dataKey="term" size="small" className="text-sm" pt={{ table: { "aria-label": caption } }}>
    <Column field="label" header="Term" />
    <Column field="input" header="Input" />
    <Column field="target" header="Against" />
    <Column header="Result" body={(r: TermRow) => r.statement} />
    <Column header="Weight" body={(r: TermRow) => formatPercent(r.weight, 0)} />
    <Column header="Points" body={(r: TermRow) => formatNumber(r.points, 1)} />
  </DataTable>
);

const ScoreValue: React.FC<{ label: string; value: number | null }> = ({ label, value }) => (
  <div className="flex align-items-baseline gap-2 mb-2">
    <span className="text-3xl font-semibold text-900" data-testid={`score-${label}`}>
      {value == null ? "Unavailable" : formatScore(value)}
    </span>
    {value != null && <span className="text-sm text-color-secondary">points</span>}
  </div>
);

const Reasons: React.FC<{ reasons: string[] }> = ({ reasons }) => (
  <ul className="m-0 mb-3 pl-3 text-sm text-color-secondary">
    {reasons.map((r) => (
      <li key={r}>{r}</li>
    ))}
  </ul>
);

const penaltyLine = (detail: ScoreDetail | null, text: string) =>
  detail && <div className="text-sm text-color-secondary mt-2">{text}</div>;

/**
 * BOF-76 — each score with the terms that produced it (FR-050), and the model it was scored with (FR-051).
 *
 * A score whose inputs are incomplete says "Unavailable" and names what is missing (FR-052); its terms
 * are still listed, so the reader sees which inputs exist and which do not. The model's targets, weights,
 * bonus and penalties come from the API response — the page holds no copy of its own that could drift
 * from the one the server actually scored with.
 */
const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ month, parameters: p }) => {
  const { operational, monetization, overall } = month.scores;
  const reasons = unavailableReasons(month);
  const imbalance = operational && monetization ? Math.abs(operational.value - monetization.value) : null;

  const churnText =
    month.churnRate != null
      ? `Churn penalty −${formatScore(operational?.penalty)} points: ${formatPercent(month.churnRate)} of clients lost × ${p.churnPenaltySlope}, at most ${p.churnPenaltyMax}.`
      : "No churn penalty — the client count did not fall.";
  const feeGrowth = month.inputs.feeGrowth;
  const declineText =
    feeGrowth != null && feeGrowth < 0
      ? `Fee-decline penalty −${formatScore(monetization?.penalty)} points: service fee fell ${formatPercent(-feeGrowth)} × ${p.declinePenaltySlope}, at most ${p.declinePenaltyMax}.`
      : "No fee-decline penalty — the service fee did not fall.";

  return (
    <div>
      <div className="grid">
        <div className="col-12 xl:col-6">
          <Card title="Operational score" className="h-full">
            <ScoreValue label="Operational" value={operational?.value ?? null} />
            {!operational && <Reasons reasons={reasons.operational} />}
            <TermTable rows={operationalRows(month, p, operational)} caption="Operational score terms" />
            {penaltyLine(operational, churnText)}
          </Card>
        </div>
        <div className="col-12 xl:col-6">
          <Card title="Monetization score" className="h-full">
            <ScoreValue label="Monetization" value={monetization?.value ?? null} />
            {!monetization && <Reasons reasons={reasons.monetization} />}
            <TermTable rows={monetizationRows(month, p, monetization)} caption="Monetization score terms" />
            {penaltyLine(monetization, declineText)}
          </Card>
        </div>
        <div className="col-12">
          <Card title="Overall score">
            <ScoreValue label="Overall" value={overall} />
            {overall == null ? (
              <div className="text-sm text-color-secondary">
                Unavailable because {!operational && !monetization ? "both scores are" : !operational ? "the Operational score is" : "the Monetization score is"}{" "}
                unavailable.
              </div>
            ) : (
              <div className="text-sm text-color-secondary">
                {overallFormula(p, operational.value, monetization.value)} = {formatScore(overall)}. The two scores
                differ by {formatScore(imbalance)} points, which costs {formatScore(imbalance * p.overallBlend.imbalancePenalty)}{" "}
                points, so one strong dimension cannot mask a weak one.
              </div>
            )}
          </Card>
        </div>
      </div>

      <Panel header={`Score model · revised ${p.revisedAt}`} toggleable className="mt-3">
        <div className="grid text-sm">
          <div className="col-12 md:col-6">
            <div className="font-semibold mb-2">Operational</div>
            <ul className="m-0 pl-3 line-height-3">
              {(["activation", "cpOnlinePerClient", "cpTotalPerClient", "clientGrowth"] as const).map((t) => (
                <li key={t}>
                  {TERM_LABEL[t]}: target{" "}
                  {t === "activation" || t === "clientGrowth"
                    ? formatPercent(p.operationalTargets[t], 0)
                    : formatNumber(p.operationalTargets[t], 1)}
                  , weight {formatPercent(p.operationalWeights[t], 0)}
                </li>
              ))}
              <li>
                Churn penalty: {p.churnPenaltySlope} × share of clients lost, at most {p.churnPenaltyMax} points
              </li>
            </ul>
          </div>
          <div className="col-12 md:col-6">
            <div className="font-semibold mb-2">Monetization</div>
            <ul className="m-0 pl-3 line-height-3">
              <li>
                {TERM_LABEL.feeGrowth}: target {formatPercent(p.feeGrowthTarget, 0)}, weight{" "}
                {formatPercent(p.monetizationWeights.feeGrowth, 0)}
              </li>
              <li>
                {TERM_LABEL.feePerClient}: against its trailing {p.baselineMonths}-month average, weight{" "}
                {formatPercent(p.monetizationWeights.feePerClient, 0)}
              </li>
              <li>
                {TERM_LABEL.feePerCpTotal}: against its trailing {p.baselineMonths}-month average, weight{" "}
                {formatPercent(p.monetizationWeights.feePerCpTotal, 0)}
              </li>
              <li>
                Fee-decline penalty: {p.declinePenaltySlope} × fee decline, at most {p.declinePenaltyMax} points
              </li>
            </ul>
          </div>
          <div className="col-12">
            Each term earns full credit at its target. Above target it keeps earning, at {formatPercent(p.excessBonusRate, 0)}{" "}
            of the excess — a soft bonus with no cap. Overall = {overallFormula(p, null, null)}.
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ScoreBreakdown;
